import { providers } from "./globalSchema.mjs";
import { relations, sql } from "drizzle-orm";
import { boolean, integer, jsonb, pgPolicy, pgTable, text, timestamp, unique, uuid } from "drizzle-orm/pg-core";

//#region src/schema/authSchema.ts
/**
* Authentication schema for better-auth.
*
* To add new better-auth plugins:
* 1. Add the plugin to apps/api/src/auth.ts
* 2. Run `NODE_ENV=development npx auth@<better-auth version> generate` in apps/api/
* 3. Copy new table definitions from generated apps/api/auth-schema.ts to this file
* 4. Delete apps/api/auth-schema.ts
* 5. Run `pnpm generate` and `pnpm migrate` in packages/db/
*
* @see https://www.better-auth.com/docs/adapters/drizzle
*/
let TaskAssignee = /* @__PURE__ */ function(TaskAssignee) {
	TaskAssignee["MyTasks"] = "Assigned to me";
	TaskAssignee["EveryoneTasks"] = "Assigned to everyone";
	TaskAssignee["AgentTasks"] = "Assigned to agent";
	TaskAssignee["UnassignedTasks"] = "Unassigned";
	return TaskAssignee;
}({});
/**
* The options in the task list's status filter. A superset of `TaskStatus`:
* `ReferralNotSent` and `ReferralSent` each narrow `Action Required` by whether
* the task has a `referral_sent` activity log item — the referral still has to
* go out, or it has gone out and now needs chasing. Every task either has that
* row or does not, so the two partition `Action Required` exactly: none falls
* into both, and none into neither. Whatever either one excludes stays
* reachable under `Action Required` itself.
*
* These are filter options only. A task's status is still one of the three
* `TaskStatus` values, so nothing writes these strings to `tasks.status`.
*
* The three status values are spelled identically to their `TaskStatus`
* counterparts so that filters persisted before this enum existed still decode.
*
* These values are persisted per user in `users.status_filter`, and
* `statusFilterCodec` falls back rather than throwing on one it does not
* recognise — so changing a value silently resets the saved preference of
* everyone using it, which is worst for the people who used the option most.
*
* Each value happens to match the label shown for it today, but they are not
* the same thing: the label lives in `LeftPane`'s `selectOptions` and product
* expects to keep rewording it. When that happens, change the label alone and
* leave the value here as it is. A test pins `ReferralSent`'s spelling because
* it is the one most likely to be reworded.
*/
let TaskListFilter = /* @__PURE__ */ function(TaskListFilter) {
	TaskListFilter["AnyStatus"] = "Any Status";
	TaskListFilter["ActionRequired"] = "Action Required";
	TaskListFilter["ReferralNotSent"] = "Referral Not Sent";
	TaskListFilter["ReferralSent"] = "Referral Sent";
	TaskListFilter["Snoozed"] = "Snoozed";
	TaskListFilter["Completed"] = "Completed";
	return TaskListFilter;
}({});
const tenantAccessPolicy = pgPolicy("tenant_access_isolation", {
	as: "permissive",
	to: "public",
	for: "all",
	using: sql`tenant_id = COALESCE(current_setting('app.current_tenant_id', true)::text, '00000000-0000-0000-0000-000000000000'::text)`,
	withCheck: sql`tenant_id = COALESCE(current_setting('app.current_tenant_id', true)::text, '00000000-0000-0000-0000-000000000000'::text)`
});
const tenants = pgTable("tenants", {
	id: text("id").primaryKey().notNull(),
	name: text("name").notNull(),
	slug: text("slug").unique(),
	logo: text("logo"),
	metadata: text("metadata"),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
});
let EHRSystem = /* @__PURE__ */ function(EHRSystem) {
	EHRSystem["ECW"] = "eClinicalWorks";
	EHRSystem["Athena"] = "Athenahealth";
	EHRSystem["Azalea"] = "Azalea";
	EHRSystem["Demo"] = "Demo";
	EHRSystem["LeadingReach"] = "LeadingReach";
	return EHRSystem;
}({});
const ehrSystems = Object.values(EHRSystem);
const tenantEhrConnections = pgTable("tenant_ehr_connections", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	vendorName: text("vendor_name", { enum: ehrSystems }).notNull(),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull()
}, (_t) => [tenantAccessPolicy]).enableRLS();
const departments = pgTable("departments", {
	id: uuid("id").primaryKey().defaultRandom().notNull(),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }).notNull(),
	name: text("name").notNull(),
	externalFacingName: text("external_facing_name"),
	tenantEhrConnectionId: uuid("tenant_ehr_connection_id").references(() => tenantEhrConnections.id, { onDelete: "set null" }),
	requestingProviderId: uuid("requesting_provider_id").references(() => providers.id, { onDelete: "set null" }),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	updatedAt: timestamp("updated_at").defaultNow().$onUpdateFn(() => /* @__PURE__ */ new Date()).notNull(),
	defaultLocationPreferences: jsonb("default_location_preferences").$type(),
	allowedOrganizationIds: jsonb("allowed_organization_ids").$type(),
	faxNumber: text("fax_number"),
	phoneNumber: text("phone_number")
}, (_t) => [tenantAccessPolicy]).enableRLS();
const users = pgTable("users", {
	id: text("id").primaryKey(),
	name: text("name").notNull(),
	email: text("email").notNull(),
	emailVerified: boolean("email_verified").$defaultFn(() => false).notNull(),
	image: text("image"),
	tenantId: text("tenant_id").references(() => tenants.id, { onDelete: "cascade" }),
	departmentId: uuid("department_id").references(() => departments.id, { onDelete: "set null" }),
	taskAssigneeFilter: text("task_assignee_filter"),
	statusFilter: text("status_filter"),
	createdAt: timestamp("created_at").$defaultFn(() => /* @__PURE__ */ new Date()).notNull(),
	updatedAt: timestamp("updated_at").$defaultFn(() => /* @__PURE__ */ new Date()).notNull()
}, (table) => [unique("email_tenant_unique").on(table.email, table.tenantId)]);
const sessions = pgTable("sessions", {
	id: text("id").primaryKey(),
	expiresAt: timestamp("expires_at").notNull(),
	token: text("token").notNull().unique(),
	createdAt: timestamp("created_at").notNull(),
	updatedAt: timestamp("updated_at").notNull(),
	ipAddress: text("ip_address"),
	userAgent: text("user_agent"),
	userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
	activeOrganizationId: text("active_organization_id").references(() => tenants.id, { onDelete: "cascade" })
});
const accounts = pgTable("accounts", {
	id: text("id").primaryKey(),
	accountId: text("account_id").notNull(),
	providerId: text("provider_id").notNull(),
	userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
	accessToken: text("access_token"),
	refreshToken: text("refresh_token"),
	idToken: text("id_token"),
	accessTokenExpiresAt: timestamp("access_token_expires_at"),
	refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
	scope: text("scope"),
	password: text("password"),
	createdAt: timestamp("created_at").notNull(),
	updatedAt: timestamp("updated_at").notNull()
});
const verifications = pgTable("verifications", {
	id: text("id").primaryKey(),
	identifier: text("identifier").notNull(),
	value: text("value").notNull(),
	expiresAt: timestamp("expires_at").notNull(),
	createdAt: timestamp("created_at").$defaultFn(() => /* @__PURE__ */ new Date()),
	updatedAt: timestamp("updated_at").$defaultFn(() => /* @__PURE__ */ new Date())
});
const apikeys = pgTable("apikeys", {
	id: text("id").primaryKey(),
	configId: text("config_id").default("default").notNull(),
	name: text("name"),
	start: text("start"),
	prefix: text("prefix"),
	key: text("key").notNull(),
	userId: text("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
	refillInterval: integer("refill_interval"),
	refillAmount: integer("refill_amount"),
	lastRefillAt: timestamp("last_refill_at"),
	enabled: boolean("enabled").$defaultFn(() => true),
	rateLimitEnabled: boolean("rate_limit_enabled").$defaultFn(() => true),
	rateLimitTimeWindow: integer("rate_limit_time_window").$defaultFn(() => 864e5),
	rateLimitMax: integer("rate_limit_max").$defaultFn(() => 1e4),
	requestCount: integer("request_count").$defaultFn(() => 0),
	remaining: integer("remaining"),
	lastRequest: timestamp("last_request"),
	expiresAt: timestamp("expires_at"),
	createdAt: timestamp("created_at").notNull(),
	updatedAt: timestamp("updated_at").notNull(),
	permissions: text("permissions"),
	metadata: text("metadata")
});
const members = pgTable("members", {
	id: text("id").primaryKey(),
	organizationId: text("organization_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
	userId: text("user_id").notNull().unique().references(() => users.id, { onDelete: "cascade" }),
	role: text("role").default("member").notNull(),
	createdAt: timestamp("created_at").notNull()
});
const invitations = pgTable("invitations", {
	id: text("id").primaryKey(),
	organizationId: text("organization_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
	email: text("email").notNull(),
	role: text("role"),
	status: text("status").default("pending").notNull(),
	expiresAt: timestamp("expires_at").notNull(),
	createdAt: timestamp("created_at").defaultNow().notNull(),
	inviterId: text("inviter_id").notNull().references(() => users.id, { onDelete: "cascade" })
});
const ssoProviders = pgTable("sso_providers", {
	id: text("id").primaryKey(),
	issuer: text("issuer").notNull(),
	oidcConfig: text("oidc_config"),
	samlConfig: text("saml_config"),
	userId: text("user_id").references(() => users.id, { onDelete: "cascade" }),
	providerId: text("provider_id").notNull().unique(),
	organizationId: text("organization_id").references(() => tenants.id, { onDelete: "cascade" }),
	domain: text("domain").notNull(),
	organizationSlug: text("organization_slug")
});
const tenantsRelations = relations(tenants, ({ many }) => ({
	departments: many(departments),
	users: many(users),
	members: many(members),
	invitations: many(invitations),
	ssoProviders: many(ssoProviders)
}));
const usersRelations = relations(users, ({ one }) => ({
	tenant: one(tenants, {
		fields: [users.tenantId],
		references: [tenants.id]
	}),
	department: one(departments, {
		fields: [users.departmentId],
		references: [departments.id]
	}),
	member: one(members, {
		fields: [users.id],
		references: [members.userId]
	})
}));
const membersRelations = relations(members, ({ one }) => ({
	organization: one(tenants, {
		fields: [members.organizationId],
		references: [tenants.id]
	}),
	user: one(users, {
		fields: [members.userId],
		references: [users.id]
	})
}));
const invitationsRelations = relations(invitations, ({ one }) => ({
	organization: one(tenants, {
		fields: [invitations.organizationId],
		references: [tenants.id]
	}),
	inviter: one(users, {
		fields: [invitations.inviterId],
		references: [users.id]
	})
}));
const ssoProvidersRelations = relations(ssoProviders, ({ one }) => ({
	organization: one(tenants, {
		fields: [ssoProviders.organizationId],
		references: [tenants.id]
	}),
	user: one(users, {
		fields: [ssoProviders.userId],
		references: [users.id]
	})
}));

//#endregion
export { EHRSystem, TaskAssignee, TaskListFilter, accounts, apikeys, departments, ehrSystems, invitations, invitationsRelations, members, membersRelations, sessions, ssoProviders, ssoProvidersRelations, tenantAccessPolicy, tenantEhrConnections, tenants, tenantsRelations, users, usersRelations, verifications };